import React, { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Skeleton from "@mui/material/Skeleton";

import dayjs from "dayjs";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FormHelperText } from "@mui/material";

/**
 * Props
 * --------------------------------------------------------
 * placeholder         – placeholder text
 * type                – "text" | "password" | "date" | …
 * value               – current value
 * onChange            – (event) => void
 * maxwidth            – max‑width for the input (CSS value, default "none")
 * name                – form field name
 * error               – boolean error state
 * inputProps          – extra props forwarded to <input>
 * required            – HTML5 required flag
 * allowOnlyNumbers    – strip non‑numeric chars on input
 * capAt               – (number) if allowOnlyNumbers is true, restricts value to this maximum
 * loading             – show skeleton while true
 * inputRef            – forwarded ref
 * isDisabled          – disabled flag
 * maxLength / minLength – character limits for text / password
 * dateRestriction     – "none" | "future" | "past"  (applies only to type="date")
 * --------------------------------------------------------
 */

const TextInput = ({
  placeholder,
  type = "text",
  value,
  onChange,
  customStyle,
  maxwidth = "none",
  multiline,
  rows,
  name,
  error = false,
  inputProps = {},
  required = false,

  loading = false,
  inputRef,
  isDisabled = false,
  maxLength = 100,
  allowOnlyNumbers = false,
  capAt,
  minLength,
  dateRestriction = "none", // 'none' | 'future' | 'past'
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  const [showCapWarning, setShowCapWarning] = useState(false);

  const handleChange = (e) => {
    if (allowOnlyNumbers) {
      let newValue = e.target.value.replace(/[^0-9]/g, "");
      if (newValue) {
        let numericValue = parseInt(newValue, 10);
        if (capAt !== undefined && !isNaN(numericValue)) {
          if (numericValue > capAt) {
            numericValue = capAt;
            setShowCapWarning(true);
          } else {
            setShowCapWarning(false);
          }
        }
        newValue = numericValue.toString();
      } else {
        setShowCapWarning(false);
      }
      onChange({ target: { name, value: newValue } });
    } else {
      onChange(e);
    }
  };

  const getTodayISO = () => new Date().toISOString().split("T")[0]; // YYYY‑MM‑DD

  /* ----- skeleton while loading ----- */
  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={40}
        sx={{ borderRadius: 1 }}
      />
    );
  }

  /* ----- consolidate inputProps ----- */
  const computedInputProps = {
    ...inputProps,
    // length limits (text & password only)
    maxLength:
      (type === "text" ||
        type === "password" ||
        type == "email" ||
        type == "tel") &&
      maxLength !== undefined
        ? maxLength
        : undefined,
    minLength:
      (type === "text" ||
        type === "password" ||
        type == "email" ||
        type == "tel") &&
      minLength !== undefined
        ? minLength
        : undefined,
    // date restrictions
    min:
      type === "date" && dateRestriction === "future"
        ? getTodayISO()
        : undefined,
    max:
      type === "date" && dateRestriction === "past" ? getTodayISO() : undefined,
  };

  /* ----- styles ----- */
  const ModifiedTextField = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1,
      borderWidth: 1,
      fontFamily: "Geist, sans-serif",
      fontSize: 16,
      maxWidth: maxwidth,
      height: multiline ? "auto" : 40, // <- dynamic height based on multiline
      alignItems: multiline ? "start" : "center", // ensures proper alignment for multiline
      "& fieldset": {
        borderColor: error ? "red" : "#C0C0C0",
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: error ? "red" : "#C0C0C0",
      },
      "&.Mui-focused fieldset": {
        borderColor: error ? "red" : "#aac7e7",
        borderWidth: 2,
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "#272727",
    },
    "& .MuiInputLabel-root": {
      color: error ? "red" : "#C0C0C0",
    },
    "& .MuiInputBase-input::placeholder": {
      color: error ? "red" : "grey",
    },
    ...customStyle,
  };

  if (type === "date") {
    const today = dayjs();

    let minDate = dayjs("1900-01-01");
    let maxDate = dayjs("2099-12-31");

    if (dateRestriction === "future") {
      minDate = today;
    } else if (dateRestriction === "past") {
      maxDate = today;
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={value ? dayjs(value) : null}
          format="DD-MM-YYYY"
          onChange={(newValue) => {
            onChange({
              target: {
                name,
                value: newValue ? newValue.format("YYYY-MM-DD") : "",
              },
            });
          }}
          minDate={minDate}
          maxDate={maxDate}
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small",
              required,
              error,
            },
          }}
          PopperProps={{
            style: { zIndex: 2000 },
          }}
        />
      </LocalizationProvider>
    );
  } else if (type === "time") {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          value={value ? dayjs(value, "HH:mm") : null}
          format="hh:mm A" // 12-hour format with AM/PM
          onChange={(newValue) => {
            onChange({
              target: {
                name,
                value: newValue ? newValue.format("hh:mm A") : "",
              },
            });
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small",
              required,
              error,
              placeholder,
            },
          }}
          PopperProps={{
            style: { zIndex: 2000 },
          }}
        />
      </LocalizationProvider>
    );
  } else {
    return (
      <>
        <TextField
          type={
            type === "password" ? (showPassword ? "text" : "password") : type
          }
          value={value}
          onChange={handleChange}
          name={name}
          placeholder={placeholder}
          variant="outlined"
          autoComplete="off"
          size="small"
          fullWidth
          multiline={multiline}
          rows={rows}
          disabled={isDisabled}
          required={required}
          inputMode={allowOnlyNumbers ? "numeric" : "text"}
          pattern={allowOnlyNumbers ? "[0-9]*" : undefined}
          inputRef={inputRef}
          inputProps={computedInputProps}
          sx={{
            ...ModifiedTextField,
            "& .MuiInputBase-input":
              type === "date" ? { color: error ? "#FF9393" : undefined } : {},
          }}
          InputProps={{
            endAdornment:
              type === "password" ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ) : undefined,
          }}
        />
        {showCapWarning && (
          <FormHelperText error>Max allowed value is {capAt}</FormHelperText>
        )}
      </>
    );
  }
};

export default TextInput;
